import { z } from "zod";
import { IsoTimestamp, MatchId, PlayerId } from "./ids.js";

// ─────────────────────────────────────────────────────────────────────────────
// LOOP 4 — THE TRUST LAYER (FAIR RESULT + UPSTAIRS). Evidence pipeline, the
// evidence agent (shadow→assist→auto, a data-earned promotion), the fraud
// battery, and the confidence ledger (§6 delta table).
// ─────────────────────────────────────────────────────────────────────────────

// ── Confidence delta table (FAIR RESULT §6 — the target state, reason-coded) ──
export const ConfidenceReason = z.enum([
  "completed_verified", // +2 the heartbeat
  "big_night_finish", // +3 top-20
  "fast_verify", // +1 opponent verifies ≤5min
  "concede_first", // 0 first time
  "concede_repeat", // −3 pattern priced
  "arrive_late", // −2
  "no_show", // −8
  "excessive_pausing", // −3
  "lose_dispute_evidence", // −6
  "fabricated_evidence", // −15 + review
  "frivolous_dispute", // −4
  "dispute_won", // +2 + restore
  "graceful_acceptance", // +1
]);
export type ConfidenceReason = z.infer<typeof ConfidenceReason>;

// The launch config values (mirrored in config 'confidence.deltas'; the service
// reads config, this is the documented default + the compile-time source).
export const CONFIDENCE_DELTAS: Record<ConfidenceReason, number> = {
  completed_verified: 2,
  big_night_finish: 3,
  fast_verify: 1,
  concede_first: 0,
  concede_repeat: -3,
  arrive_late: -2,
  no_show: -8,
  excessive_pausing: -3,
  lose_dispute_evidence: -6,
  fabricated_evidence: -15,
  frivolous_dispute: -4,
  dispute_won: 2,
  graceful_acceptance: 1,
};

/**
 * The plain line each reason shows on a player's record.
 *
 * Lives HERE, beside the reasons and their deltas, because two consumers need
 * the same words: the Confidence screen renders them as history, and the engine
 * puts them in the notification that tells a player their record moved. Two
 * copies of this map is two vocabularies for the same event — the player is
 * told one thing in a push and reads another when they tap it.
 */
export const CONFIDENCE_REASON_LABEL: Record<ConfidenceReason, string> = {
  completed_verified: "Match played and verified",
  big_night_finish: "Top finish on the night",
  fast_verify: "Opponent verified fast",
  concede_first: "Conceded a score",
  concede_repeat: "Conceded again",
  arrive_late: "Arrived late",
  no_show: "No-show",
  excessive_pausing: "Excessive pausing",
  lose_dispute_evidence: "Lost a review on evidence",
  fabricated_evidence: "Fabricated evidence",
  frivolous_dispute: "Review raised and lost",
  dispute_won: "Review won — you were right",
  graceful_acceptance: "Accepted the call",
};

export const ConfidenceLine = z.object({
  ts: IsoTimestamp,
  delta: z.number().int(),
  // Usually a ConfidenceReason, but the ledger also carries compound codes the
  // verdict path mints (e.g. "sanction.score_fabrication"), so the player-facing
  // line accepts any reason string rather than 500-ing on an unknown one.
  reason: z.string(),
  match_id: MatchId.nullable(),
  balance_after: z.number().int(),
});
export type ConfidenceLine = z.infer<typeof ConfidenceLine>;

// GET /confidence — the player-facing "why is my score X" history (§6).
export const ConfidenceView = z.object({
  balance: z.number().int(),
  sent_home: z.boolean(), // below the matchmaking-lock floor
  lines: z.array(ConfidenceLine),
});
export type ConfidenceView = z.infer<typeof ConfidenceView>;

// ── Evidence (FAIR RESULT §3 — compressed client-side, hashed, async) ────────
export const EvidenceObject = z.object({
  id: z.string().uuid(),
  match_id: MatchId,
  player_id: PlayerId,
  storage_key: z.string(),
  sha256: z.string().length(64),
  phash: z.string(), // 64-bit perceptual hash as hex/decimal string
  width: z.number().int().min(1),
  height: z.number().int().min(1),
  bytes: z.number().int().min(1),
  uploaded_at: IsoTimestamp,
});
export type EvidenceObject = z.infer<typeof EvidenceObject>;

/**
 * POST /evidence/upload-url — a one-object, short-lived write capability.
 *
 * The client sends only its sha256; the SERVER derives `storage_key`. A client
 * that could name its own key could write into another match's evidence prefix,
 * and evidence a stranger can overwrite is not evidence.
 */
export const EvidenceUploadUrl = z.object({
  /** Absolute URL to PUT the compressed JPEG to. Expires — see expires_in_s. */
  url: z.string().url(),
  /** Send this back with the metadata once the PUT succeeds. */
  storage_key: z.string().min(1),
  expires_in_s: z.number().int().positive(),
});
export type EvidenceUploadUrl = z.infer<typeof EvidenceUploadUrl>;

// ── The Evidence Agent (FAIR RESULT §5) ──────────────────────────────────────
// Extraction schema per screenshot (§5.1).
export const AgentExtraction = z.object({
  home_name: z.string().nullable(),
  away_name: z.string().nullable(),
  home_score: z.number().int().nullable(),
  away_score: z.number().int().nullable(),
  ui_frame: z.enum(["fulltime", "stats", "other"]),
  visible_match_context: z.string().nullable(),
});
export type AgentExtraction = z.infer<typeof AgentExtraction>;

// A signed signal row in the agent's breakdown (the plain rows the DUGOUT shows).
export const AgentSignal = z.object({
  key: z.string(), // 'names_match' | 'scores_mirror' | 'screenshot_reused' | 'phash_collision' | …
  label: z.string(), // "Names match registered ✓"
  score: z.number(), // signed contribution
  flag: z.enum(["ok", "warn", "red"]),
});
export type AgentSignal = z.infer<typeof AgentSignal>;

// The agent's proposal. Below-threshold confidence ALWAYS escalates (§5.1 —
// the agent is never allowed to guess on a player's record).
export const AgentVerdict = z.object({
  match_id: MatchId,
  mode: z.enum(["shadow", "assist", "auto"]),
  proposal: z.enum(["confirm", "resolve_for_host", "resolve_for_joiner", "escalate"]),
  confidence: z.number().min(0).max(1),
  signals: z.array(AgentSignal),
  extraction: z.array(AgentExtraction),
  model: z.string(),
  run_at: IsoTimestamp,
});
export type AgentVerdict = z.infer<typeof AgentVerdict>;

// ── Fraud radar (FAIR RESULT §5.2 + §8) ──────────────────────────────────────
export const FraudSignalType = z.enum([
  "phash_collision", // the same screenshot for two matches — the cheapest catch
  "correction_bias", // corrections always in the submitter's favour
  "repeat_pair", // pairs repeatedly meeting through "empty" windows
  "device_graph", // same device/number fingerprints (Union-Find cluster)
  "noshow_repeater",
]);
export type FraudSignalType = z.infer<typeof FraudSignalType>;

export const FraudSignal = z.object({
  id: z.string().uuid(),
  type: FraudSignalType,
  subjects: z.array(PlayerId),
  detail: z.string(),
  raised_at: IsoTimestamp,
  reviewed: z.boolean(),
});
export type FraudSignal = z.infer<typeof FraudSignal>;

// The Union-Find collusion cluster (device/number/mutual-walkover/repeat-pair).
export const FraudCluster = z.object({
  cluster_id: z.string(),
  members: z.array(PlayerId),
  edge_types: z.array(z.string()),
  size: z.number().int().min(2),
});
export type FraudCluster = z.infer<typeof FraudCluster>;

// GET /dugout/fraud → the radar feed (S4).
export const FraudRadarView = z.object({
  signals: z.array(FraudSignal),
  clusters: z.array(FraudCluster),
});
export type FraudRadarView = z.infer<typeof FraudRadarView>;

// AgentVerdict is imported by the trust service; VarVerdict/VarReasonCode come
// from match.js via the barrel (not re-exported here to avoid a duplicate).

// ── Enriched VAR case (the DUGOUT case room, Loop 4 — the agent proposal +
// evidence + forensics fill the shadow-mode slot from Loop 1) ────────────────
export const VarForensics = z.object({
  evidence: z.array(EvidenceObject),
  agent: AgentVerdict.nullable(), // null until the agent has run
  phash_collision: z.boolean(),
});
export type VarForensics = z.infer<typeof VarForensics>;
